import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faClock, faSyncAlt, faVideo } from "@fortawesome/free-solid-svg-icons";
import Swal from "sweetalert2";
import localForage from "localforage";
import PatientSidebar from "./PatientSidebar";
import JitsiMeetingEmbed from "../institutions/dashboard_pages/Telehealth/JitsiMeetingEmbed";
import { buildApiUrl, getAuthToken, useBaseUrl } from "../utils/utils";

interface PatientSessionInfo {
    id: string;
    title: string;
    description?: string;
    scheduled_at: string;
    duration: number;
    status: string;
    provider_name: string;
    clinic_name: string;
    collaboration_name?: string;
    notes?: string;
    room_name: string;
}

const PatientTelehealthSessionPage: React.FC = () => {
    const { sessionId } = useParams<{ sessionId: string }>();
    const navigate = useNavigate();
    const baseUrl = useBaseUrl();

    const [sessionInfo, setSessionInfo] = useState<PatientSessionInfo | null>(null);
    const [loading, setLoading] = useState(true);
    const [patientName, setPatientName] = useState("Patient");
    const [patientEmail, setPatientEmail] = useState("");

    useEffect(() => {
        document.title = "Patient Telehealth Session - Uzima Healthcare";
        initialize();
    }, [sessionId]);

    const initialize = async () => {
        if (!sessionId) return;
        try {
            setLoading(true);
            const patientId = await localForage.getItem("patient_id");
            const loginInfo: any = await localForage.getItem("uzima_login_info");
            const token = await getAuthToken();
            const headers = {
                "Content-Type": "application/json",
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
            };
            const response = await fetch(buildApiUrl(baseUrl, `collaborations/api/patient/telehealth/session/${sessionId}/?patient_id=${patientId}`), { headers });
            const data = await response.json();
            const resolvedSession = data?.session || data?.data?.session;
            if (!response.ok || !resolvedSession) {
                throw new Error(data?.message || "Unable to load telehealth session");
            }
            setSessionInfo(resolvedSession);
            setPatientName(loginInfo?.fname && loginInfo?.lname ? `${loginInfo.fname} ${loginInfo.lname}` : loginInfo?.full_name || "Patient");
            setPatientEmail(loginInfo?.email || "");

            await fetch(buildApiUrl(baseUrl, `collaborations/telehealth-sessions/${sessionId}/join/`), {
                method: "POST",
                headers,
                body: JSON.stringify({ user_type: "patient", role: "participant" }),
            });
        } catch (error: any) {
            console.error("Patient telehealth initialization error:", error);
            Swal.fire({ icon: "error", title: "Session unavailable", text: error?.message || "Unable to initialize the telehealth session." }).then(() => {
                navigate("/patient/telehealth");
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="d-flex" style={{ minHeight: "100vh", background: "#f8fafc" }}>
            <PatientSidebar />
            <main className="flex-grow-1">
                <div className="sc-wrap">
                    <div className="sc-header">
                        <div>
                            <h1>{sessionInfo?.title || "Telehealth Session"}</h1>
                            <p>{sessionInfo ? `${sessionInfo.provider_name} · ${sessionInfo.clinic_name}` : "Preparing your secure consultation room"}</p>
                        </div>
                        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                            <button className="sc-btn sc-btn-outline" onClick={() => navigate("/patient/telehealth")}>
                                <FontAwesomeIcon icon={faArrowLeft} /> Back to telehealth
                            </button>
                            <button className="sc-btn sc-btn-primary" onClick={initialize}>
                                <FontAwesomeIcon icon={faSyncAlt} /> Reload
                            </button>
                        </div>
                    </div>
                    {loading || !sessionInfo ? (
                        <div className="sc-spinner"></div>
                    ) : (
                        <>
                            <div className="sc-stats">
                                <div className="sc-stat">
                                    <div className="sc-stat-icon teal"><FontAwesomeIcon icon={faVideo} /></div>
                                    <div className="sc-stat-value">{sessionInfo.status}</div>
                                    <div className="sc-stat-label">Status</div>
                                </div>
                                <div className="sc-stat">
                                    <div className="sc-stat-icon blue"><FontAwesomeIcon icon={faClock} /></div>
                                    <div className="sc-stat-value">{sessionInfo.duration} min</div>
                                    <div className="sc-stat-label">Duration</div>
                                </div>
                            </div>
                            <div className="sc-split">
                                <div>
                                    <JitsiMeetingEmbed
                                        roomName={sessionInfo.room_name || `uzima-${sessionInfo.id}`}
                                        displayName={patientName}
                                        email={patientEmail}
                                        subject={sessionInfo.title}
                                        onClose={() => navigate("/patient/telehealth")}
                                    />
                                </div>
                                <div className="sc-panel">
                                    <div className="sc-panel-head">
                                        <div>
                                            <h3>Appointment summary</h3>
                                            <p>Details for your current telehealth consultation.</p>
                                        </div>
                                    </div>
                                    <div className="sc-panel-body">
                                        <div className="sc-list-stack">
                                            <div className="sc-list-item"><div><strong>Provider</strong><small style={{ display: "block", marginTop: 4 }}>{sessionInfo.provider_name}</small></div></div>
                                            <div className="sc-list-item"><div><strong>Clinic</strong><small style={{ display: "block", marginTop: 4 }}>{sessionInfo.clinic_name}</small></div></div>
                                            <div className="sc-list-item"><div><strong>Scheduled time</strong><small style={{ display: "block", marginTop: 4 }}>{new Date(sessionInfo.scheduled_at).toLocaleString()}</small></div></div>
                                            {sessionInfo.collaboration_name && <div className="sc-list-item"><div><strong>Collaboration</strong><small style={{ display: "block", marginTop: 4 }}>{sessionInfo.collaboration_name}</small></div></div>}
                                            {sessionInfo.description && <div className="sc-list-item"><div><strong>Description</strong><small style={{ display: "block", marginTop: 4 }}>{sessionInfo.description}</small></div></div>}
                                            {sessionInfo.notes && <div className="sc-list-item"><div><strong>Notes</strong><small style={{ display: "block", marginTop: 4 }}>{sessionInfo.notes}</small></div></div>}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </main>
        </div>
    );
};

export default PatientTelehealthSessionPage;
